// import React, { useState, useEffect } from 'react';
import React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
// import LogoutIcon from '@mui/icons-material/Logout';
// import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import Logo from '../../../assets/AptiviewLogo.png';
import AvatarImg from '../../../assets/avatar.jpg';
// import { AssessmentOutlined } from '@mui/icons-material';
// import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
// import DynamicBasicDialog from '../../admin/component/DynamicBasicDialog';
import NotificationBell from '../../NotificationBell';
import { useUser } from '../../../contexts/userContext';
import useCompanyData from '../../../hooks/useCompanyData';

export default function MinimalNavbar({ onLogout, onToggleSidebar }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { userDetails } = useUser();
  const { company, companyLogo } = useCompanyData();

  const getProfileImage = () => {
    // Helper to construct URL
    const getUrl = (url) => {
      if (!url) return null;
      if (url.startsWith("http")) return url;
      const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/?api\/?$/i, '');
      const base = apiBase || (process.env.REACT_APP_FRONTEND_URL || '').replace(/\/$/, '');
      return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    if (userDetails?.user_profile_picture) return getUrl(userDetails.user_profile_picture);
    if (userDetails?.profile_picture) return getUrl(userDetails.profile_picture);
    if (userDetails?.profile?.current_photo) return getUrl(userDetails.profile.current_photo);

    return AvatarImg;
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(229, 229, 229, 0.3)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          color: '#262626',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: { xs: '56px', sm: '64px' },
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: '56px !important', sm: '64px !important' },
            px: { xs: 1.5, sm: 2, md: 3 },
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 2 }
          }}
        >
          {/* Left side - Menu toggle and Logo */}
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 1, sm: 1.5, md: 2 }}
            sx={{ flex: { xs: '0 0 auto', sm: 'none' }, minWidth: 0 }}
          >
            {onToggleSidebar && (
              <IconButton
                onClick={onToggleSidebar}
                sx={{
                  color: '#525252',
                  padding: { xs: '8px', sm: '12px' },
                  '&:hover': {
                    background: 'rgba(14, 165, 233, 0.1)',
                    color: '#0ea5e9'
                  },
                  display: { xs: 'flex', md: 'none' } // Show on mobile/tablet, hide on desktop
                }}
              >
                <MenuIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </IconButton>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1.5 },
                cursor: 'pointer',
                flexShrink: 0
              }}
              onClick={() => {
                if (window.hasUnsavedQuestionnaireChanges) {
                  if (!window.confirm("You have unsaved questionnaire changes. Are you sure you want to leave?")) {
                    return;
                  }
                  window.hasUnsavedQuestionnaireChanges = false;
                }
                navigate('/dashboard');
              }}
            >
              {/* Aptiview Logo */}
              <img
                src={Logo}
                alt="Aptiview Logo"
                style={{
                  height: isMobile ? '24px' : isTablet ? '28px' : '30px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />

              {/* Separator and Company Logo */}
              {companyLogo && (
                <>
                  <Box
                    sx={{
                      height: { xs: '20px', sm: '24px' },
                      width: '1px',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      mx: { xs: 0.5, sm: 1 }
                    }}
                  />
                  <img
                    src={companyLogo}
                    alt={company?.name || "Company Logo"}
                    style={{
                      height: isMobile ? '24px' : isTablet ? '28px' : '30px',
                      width: 'auto',
                      maxWidth: '200px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      // Hide company logo if it fails to load
                      e.target.style.display = 'none';
                    }}
                  />
                </>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pl: { xs: 0.5, sm: 1 },
                flexShrink: 0
              }}
            >
            </Box>
          </Box>

          {/* Right side - Notifications and Profile */}
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 0.5, sm: 1, md: 2 }}
            sx={{ flexShrink: 0 }}
          >
            <NotificationBell />

            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                    {userDetails?.firstName} {userDetails?.lastName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {userDetails?.email}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', mt: 0.5 }}>
                    {localStorage.getItem('role') || 'Candidate'}
                  </Typography>
                </Box>
              }
              placement="bottom"
              arrow
            >
              <Box
                sx={{
                  p: { xs: 0.25, sm: 0.5 },
                  borderRadius: { xs: '8px', sm: '12px' },
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(229, 229, 229, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'rgba(14, 165, 233, 0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onClick={() => {
                  if (window.hasUnsavedQuestionnaireChanges) {
                    if (!window.confirm("You have unsaved questionnaire changes. Are you sure you want to leave?")) {
                      return;
                    }
                    window.hasUnsavedQuestionnaireChanges = false;
                  }
                  navigate('/profile');
                }}
              >
                <Avatar
                  src={getProfileImage()}
                  alt="avatar"
                  sx={{
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    border: '2px solid rgba(255, 255, 255, 0.5)'
                  }}
                />
              </Box>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
}
















